import React, { useState, useCallback, useEffect } from 'react'
import { Button, Select } from 'sputnik-ui'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Zoom,
  DialogContentText,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import { isBeward, isEltex } from 'utils/thirdPartyIntercomKinds'

import { unwrapResult } from '@reduxjs/toolkit'
import {
  fetchAvailable,
  setSearch,
  linkDevice,
  importDeviceKeys,
} from './installationsSlice'
import DeviceListItem from './DeviceListItem'
import EltexKeysModal from './EltexKeysModal'
import {
  CameraNoticeField,
  CAMERA_NOTICE_MAX_LENGTH,
} from './CameraNoticeField'

function Transition(props) {
  return <Zoom direction="up" {...props} />
}

export default function AttachNewDeviceModal({
  handleClose,
  open,
  lat,
  lon,
  onSuccess,
  ...props
}) {
  const [selected, setSelected] = useState()
  const [notice, setNotice] = useState('')
  const [keysModalOpen, setKeysModalOpen] = useState(false)
  const [importPending, setImportPending] = useState(false)
  // Подсказка под выбором устройства: Beward стирает 3-значные коды, Eltex — без камеры и кодов.
  const [installHint, setInstallHint] = useState(null)
  const handleChangeSelected = (device) => {
    if (isBeward(device.model)) {
      setInstallHint('install_beward_warn')
    } else if (isEltex(device.model)) {
      setInstallHint('install_eltex_hint')
    } else {
      setInstallHint(null)
    }
    setSelected(device)
  }

  const dispatch = useDispatch()
  const { entry, house } = useParams()
  const linkPending = useSelector(
    ({ installationsNew }) => installationsNew.linkPending
  )
  const isGate = selected?.type === 'intercom' ? !entry : false
  const supportsCameraNotice = selected?.type === 'camera' || isGate

  const availableDevices = useSelector(
    ({ installationsNew }) => installationsNew.available
  )
  const search = useSelector(({ installationsNew }) => installationsNew.search)
  const loadingAvailable = useSelector(
    ({ installationsNew }) => installationsNew.loadingAvailable
  )

  // Импорт идемпотентен: он сравнивает ключи с уже имеющимися на подъезде,
  // поэтому повторный вызов после привязки безопасен.
  const importKeys = useCallback(
    (file) =>
      dispatch(
        importDeviceKeys({ uuid: selected?.value, entryUuid: entry, file })
      ).then(unwrapResult),
    [dispatch, entry, selected]
  )

  // pendingKeys — ключи, которые подъезд не смог принять до привязки (у него ещё не
  // было точки установки). Повторяем импорт сразу после link_device: список ключей
  // такого подъезда пуст, поэтому синхронизация с устройства ничего не удаляет.
  const attachIntercom = useCallback(
    (pendingKeys = null) => {
      const params = {
        house_uuid: house,
        entry_uuid: entry,
        identifier: selected?.value,
        gate_lat: lat,
        gate_lon: lon,
        device_type: isGate ? 'gate' : selected.type,
      }

      if (supportsCameraNotice) {
        params.notice = notice.trim()
      }

      dispatch(linkDevice(params))
        .then(unwrapResult)
        .then(() => (pendingKeys ? importKeys(pendingKeys.file) : null))
        .then(() => {
          setSelected(null)
          setNotice('')
          handleClose()
          onSuccess()
        })
        // Ошибку показывает thunk; на провале импорта после привязки не закрываемся,
        // чтобы партнёр увидел сообщение и понял, что ключи не перенесены.
        .catch(() => {})
    },
    [
      dispatch,
      entry,
      handleClose,
      house,
      importKeys,
      isGate,
      lat,
      lon,
      notice,
      onSuccess,
      selected,
      supportsCameraNotice,
    ]
  )

  // Ключи, залитые в контроллер Eltex напрямую, добавляем на подъезд до привязки:
  // link_device запускает синхронизацию, и адаптер удалит с устройства всё,
  // чего нет на платформе (business_apps#1468). Калитки вне MVP — там шага нет.
  const needsKeysStep = isEltex(selected?.model) && Boolean(entry) && !isGate

  // Обещать перенос ключей можно только там, где шаг действительно выполняется:
  // на калитке его нет, и ключи контроллера там не переносятся.
  const effectiveHint =
    installHint === 'install_eltex_hint' && !needsKeysStep
      ? 'install_eltex_no_keys_warn'
      : installHint
  const hintIsWarning = effectiveHint !== 'install_eltex_hint'

  const handleConfirm = useCallback(() => {
    if (needsKeysStep) {
      setKeysModalOpen(true)
      return
    }

    attachIntercom()
  }, [attachIntercom, needsKeysStep])

  const handleKeysConfirm = useCallback(
    (file) => {
      setImportPending(true)
      importKeys(file)
        .then((payload) => {
          setKeysModalOpen(false)
          attachIntercom(payload?.deferred ? { file } : null)
        })
        // Сообщение об ошибке показывает сам thunk, модалка остаётся открытой.
        .catch(() => {})
        .finally(() => setImportPending(false))
    },
    [attachIntercom, importKeys]
  )

  const { t } = useTranslation(['common', 'devices', 'messages'])

  const [handleSearch] = useDebouncedCallback(
    (q) => dispatch(setSearch(q)),
    150
  )

  useEffect(() => {
    dispatch(fetchAvailable({ q: search }))
  }, [dispatch, search, handleSearch])

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      onClose={handleClose}
      PaperProps={{ style: { overflow: 'visible', flexBasis: '420px' } }}
    >
      <DialogTitle>{t('devices:actions.install')}</DialogTitle>
      <DialogContent style={{ overflow: 'visible' }}>
        <Select
          handleSelect={handleChangeSelected}
          selected={selected}
          placeholder={t('devices:placeholder.search')}
          isMulti={false}
          isLoading={loadingAvailable}
          options={availableDevices}
          onInputChange={handleSearch}
          filterOption={(o) => o}
          notFoundMessage={t('messages:search.empty')}
          // fixme
          // eslint-disable-next-line react/no-unstable-nested-components
          formatOptionLabel={(o) => (
            <DeviceListItem
              {...o}
              secondaryLabel={t(
                `devices:types.${
                  o.type === 'camera' ? 'camera' : entry ? 'intercom' : 'gate'
                }`
              )}
            />
          )}
        />
        {installHint && (
          <DialogContentText
            color={hintIsWarning ? 'error' : 'textSecondary'}
            style={{ marginTop: '10px' }}
          >
            {t(`devices:helper_text.${effectiveHint}`)}
          </DialogContentText>
        )}
        {supportsCameraNotice && (
          <CameraNoticeField
            style={{ marginTop: 19 }}
            value={notice}
            optional
            disabled={linkPending}
            onChange={(event) => {
              if (event.target.value.length <= CAMERA_NOTICE_MAX_LENGTH) {
                setNotice(event.target.value)
              }
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        {!linkPending && (
          <Button onClick={handleClose} secondary rounded>
            {t('common:button.cancel')}
          </Button>
        )}

        <Button
          onClick={handleConfirm}
          rounded
          primary
          variant="contained"
          disabled={linkPending || !selected}
        >
          {linkPending
            ? house
              ? t('common:loaders.gate_connect')
              : t('common:loaders.device_connect')
            : t('common:button.confirm')}
        </Button>
      </DialogActions>
      <EltexKeysModal
        open={keysModalOpen}
        uuid={selected?.value}
        pending={importPending}
        onClose={() => setKeysModalOpen(false)}
        onConfirm={handleKeysConfirm}
      />
    </Dialog>
  )
}
